import { IFormValidate } from "@ngt/forms-core";
import { asyncDebounce, IForm, IFormValidationError, IFormValidationResult, OnlinePatientManagementContext, pascalToCameCasePropertyPath } from "@ngt/opms";
import { ResponseStatus } from "@servicestack/client";
import { useCallback, useContext, useMemo } from "react";
import { EConsentUserType } from "@/contexts/EConsentContext";

interface IFormValidationResponse {
    responseStatus?: ResponseStatus;
    validationResult?: IFormValidationResult;
}

export const useEConsentFormValidate = <T extends IForm = IForm>(
    formTypeName?: string,
    consentGuid?: string,
    debounce?: number,
    userType?: EConsentUserType | null,
    patientId?: number
) => {
    const {serviceStackClient, dtos} = useContext(OnlinePatientManagementContext);

    const formDto = useMemo(() => {
        const suffix = userType === EConsentUserType.Investigator
            ? "EConsentInvestigatorPostValidate"
            : "EConsentFormPostValidate";
        return dtos[`${formTypeName}${suffix}`];
    }, [dtos, formTypeName, userType])

    const handleValidate: IFormValidate<T, IFormValidationError> = useCallback(async (formState, _formActions) => {
        if (!formDto) {
            return {}
        };

        const { value } = formState;
        const request = userType === EConsentUserType.Investigator
            ? new formDto({ patientId, form: value! })
            : new formDto({ form: value!, guid: consentGuid });
        const response: IFormValidationResponse = await serviceStackClient.post(request)
        const validationResult = response.validationResult;
        
        // parse errors into a format the form understands.
        const groupErrors = validationResult?.errors?.reduce<Record<string, IFormValidationError[]>>((a, b) => {
            const propertyName = pascalToCameCasePropertyPath(b.property!)
            if (!a[propertyName]) {
                a[propertyName] = [];
            }
            a[propertyName].push(b as IFormValidationError);
            return a;
        }, {}) ?? {};
        // return errors;
        return groupErrors;
    }, [serviceStackClient, formDto, consentGuid, patientId, userType]);

    // debounce validation functions to reduce calls to the server and associate lag.
    const debouncedValidate = useMemo(() => {
        if (debounce === 0) {
            return handleValidate;
        }

        return asyncDebounce(handleValidate, debounce ?? 500);
    }, [handleValidate, debounce]);

    return debouncedValidate;
};

export default useEConsentFormValidate;
